import { CLAUDE_MODELS, EFFORT_LEVELS, type ClaudeModel, type EffortLevel } from "@szyos/contracts";
import { EFFORT_INFO, MODEL_INFO } from "@szyos/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { Icon } from "../icons";
import { ErrorNote, useToast } from "./feedback";

/** Auswahltext mit grobem Verbrauch, Opus ist die Bezugsgröße. */
export function modelOption(m: ClaudeModel): string {
  const cost = MODEL_INFO[m].relativeCost;
  const hint = cost === 1 ? "Bezugsgröße" : `etwa ${String(cost).replace(".", ",")}-mal Opus`;
  return `${MODEL_INFO[m].label} (${hint})`;
}

/** Standard für Modell und Aufwand aller Claude-Läufe; im Plan je Aufgabe überschreibbar. */
export function AgentSettingsPanel({ isOwner }: { isOwner: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ["agent-settings"], queryFn: api.agentSettings });
  const [model, setModel] = useState<ClaudeModel>("opus");
  const [effort, setEffort] = useState<EffortLevel>("high");
  const [error, setError] = useState<string | null>(null);
  const ids = { model: useId(), effort: useId(), modelHint: useId(), effortHint: useId() };

  // Gespeicherten Stand nur übernehmen, solange der Mensch noch nichts gewählt hat: eine spät
  // eintreffende Antwort darf eine Auswahl nie überschreiben.
  const touched = useRef(false);
  useEffect(() => {
    if (settings.data && !touched.current) {
      setModel(settings.data.model);
      setEffort(settings.data.effort);
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => api.setAgentSettings({ model, effort }),
    onSuccess: async (r) => {
      touched.current = false;
      setError(null);
      toast(`Standard gespeichert: ${MODEL_INFO[r.model].label}, Aufwand ${r.effort}`);
      await queryClient.invalidateQueries({ queryKey: ["agent-settings"] });
    },
    onError: (err) => {
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const dirty = settings.data ? settings.data.model !== model || settings.data.effort !== effort : false;

  return (
    <form
      className="panel form agent-settings"
      onSubmit={submit}
      noValidate
      aria-labelledby="agentSettingsTitle"
    >
      <div className="panel-head">
        <h2 id="agentSettingsTitle">Claude: Modell und Aufwand</h2>
      </div>
      <p className="muted">
        Standard für alle Claude-Läufe. Im Plan lässt sich jede Aufgabe einzeln einstellen, dort macht die
        Beraterin einen Vorschlag.
      </p>
      <label htmlFor={ids.model}>Modell</label>
      <select
        id={ids.model}
        value={model}
        disabled={!isOwner}
        aria-describedby={ids.modelHint}
        onChange={(e) => {
          touched.current = true;
          setModel(e.target.value as ClaudeModel);
        }}
      >
        {CLAUDE_MODELS.map((m) => (
          <option key={m} value={m}>
            {modelOption(m)}
          </option>
        ))}
      </select>
      <small id={ids.modelHint} className="muted">
        {MODEL_INFO[model].use}
      </small>
      <label htmlFor={ids.effort}>Aufwand</label>
      <select
        id={ids.effort}
        value={effort}
        disabled={!isOwner}
        aria-describedby={ids.effortHint}
        onChange={(e) => {
          touched.current = true;
          setEffort(e.target.value as EffortLevel);
        }}
      >
        {EFFORT_LEVELS.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>
      <small id={ids.effortHint} className="muted">
        {EFFORT_INFO[effort]}
      </small>
      <ErrorNote error={error} />
      {isOwner ? (
        <button type="submit" className="btn btn--primary" disabled={save.isPending || !dirty}>
          <span className="btn-label">
            <Icon name="check" /> Als Standard speichern
          </span>
        </button>
      ) : (
        <p className="muted">Nur der Owner ändert den Standard.</p>
      )}
    </form>
  );
}
